import {
  BulkActionContainerCol,
  Col,
  Table,
  TableActionBarRow,
  Alert,
  DesignTokens,
  ValueOrFallback,
} from "melco-ui";
import { useTranslation } from "react-i18next";
import {
  BulkActionCount,
  EfsMelcoEfsModelsMappingListItem,
  Search,
  useTableSelection,
} from "melco-shared-logic";
import { BulkActionDeleteDataMappings } from "./BulkActionDeleteDataMappings";
import { useDataMappings } from "../../hooks/inputFormats/useDataMappings";
import styled from "styled-components";

const { spacings } = DesignTokens;

const StyledAlert = styled(Alert)`
  margin-top: ${spacings.xlarge}px;
  margin-right: ${spacings.xlarge}px;
  margin-left: ${spacings.xlarge}px;
`;

type DataMappingsListProps = {
  inputFormatId: string;
  editDataMapping: (dataMapping: EfsMelcoEfsModelsMappingListItem) => void;
};

export const DataMappingsList: React.FC<DataMappingsListProps> = ({
  inputFormatId,
  editDataMapping,
}) => {
  const {
    isLoading,
    isFetching,
    data,
    isPreviousData,
    errorComponent,
    onTableConfigChange,
    pagination,
    searchQueryChange,
    refetch,
  } = useDataMappings(inputFormatId);

  const { t } = useTranslation();

  const { selectedRowKeys, setSelectedRowKeys, rowSelection, onRow } =
    useTableSelection(editDataMapping);

  const columns = [
    {
      title: t("dataMappings.list.columns.name"),
      key: "name",
      dataIndex: "name",
      sorter: true,
      render: (_: string, item: EfsMelcoEfsModelsMappingListItem) => {
        return (
          <ValueOrFallback
            value={item.name}
            fallback={t("global.fallback.empty")}
          />
        );
      },
    },
    {
      title: t("dataMappings.list.columns.config_type"),
      key: "config_type",
      dataIndex: "config_type",
      sorter: true,
      render: (_: string, item: EfsMelcoEfsModelsMappingListItem) => {
        return (
          <ValueOrFallback
            value={
              item.config_type
                ? t(`dataMappings.types.${item.config_type}`)
                : undefined
            }
            fallback={t("global.fallback.empty")}
          />
        );
      },
    },
  ];

  return (
    <>
      <StyledAlert
        type="info"
        message={t("dataMappings.help")}
        withBackground
      />

      <TableActionBarRow>
        <Col>
          <Search
            onSearchInputChange={(query: string) => searchQueryChange(query)}
          />
        </Col>
        <BulkActionContainerCol>
          <BulkActionCount selectedCount={selectedRowKeys.length} />
          <BulkActionDeleteDataMappings
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            refetch={refetch}
          />
        </BulkActionContainerCol>
      </TableActionBarRow>
      <Table
        showSorterTooltip={false}
        sticky
        loading={isPreviousData ? isFetching : isLoading}
        dataSource={data}
        columns={columns}
        onChange={onTableConfigChange}
        pagination={pagination}
        rowKey="id"
        rowSelection={rowSelection}
        onRow={onRow}
        rowClassName={() => "clickable"}
        noDataMessage={t("dataMappings.list.empty")}
        errorComponent={errorComponent}
      />
    </>
  );
};
